import { FC, useCallback, useState } from 'react';
import { Button, Container, Title, Alert, Paper, Text } from '@mantine/core';
import { IconInfoCircle, IconCheck, IconX } from '@tabler/icons-react';
import 'assets/Form.scss';
import ContactInfo from './ContactInfo';
import GuardianInfo from './GuardianInfo';
import TableReservation from './TableReservation';
import SleepingInfo from './SleepingInfo';
import BasicInfo from './BasicInfo';
import Food from './Food';
import AttendanceDays from './AttendanceDays';
import { Form, Formik } from 'formik';
import { RegistrationDto } from 'types/common';
import * as Yup from 'yup';
import registrationService from 'services/registrationService';
import { useSettings } from '../../hooks/useSettings';
import { formatEventDateRange } from 'utils/dateUtils';
import Feedback from './Feedback';

const RegistrationSchema = Yup.object().shape({
    etunimi: Yup.string().required('Pakollinen tieto'),
    sukunimi: Yup.string().required('Pakollinen tieto'),
    puhelin: Yup.string().required('Pakollinen tieto'),
    email: Yup.string().email('Virheellinen sähköposti').required('Pakollinen tieto')
});

const RegistrationView: FC = () => {
    const { settings, loading: settingsLoading } = useSettings();
    const [showMessage, setShowMessage] = useState({ title: '', message: '', success: false });
    const initialValues: RegistrationDto = {
        etunimi: '',
        sukunimi: '',
        email: '',
        puhelin: '',
        nickname: ''
    };

    const eventTime =
        settings?.startDate && settings?.endDate ? formatEventDateRange(settings.startDate, settings.endDate) : '';

    const storeRegistration = useCallback(async (values: RegistrationDto) => {
        try {
            const res = await registrationService.register(values);
            if (res.success) {
                setShowMessage({
                    title: 'Rekisteröinti onnistui!',
                    message: 'Sähköpostiisi on lähetetty pöytävarausohjeet.',
                    success: true
                });
            } else {
                setShowMessage({
                    title: 'Rekisteröinti epäonnistui!',
                    message: '',
                    success: false
                });
            }
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'response' in err) {
                const axiosError = err as { response?: { data?: { message?: string } } };
                if (axiosError.response?.data?.message === 'already registered') {
                    setShowMessage({
                        title: 'Rekisteröinti epäonnistui!',
                        message: 'Sähköpostilla on jo rekisteröity ilmoittautuminen.',
                        success: false
                    });
                } else {
                    setShowMessage({
                        title: 'Rekisteröinti epäonnistui!',
                        message: '',
                        success: false
                    });
                }
            } else {
                setShowMessage({
                    title: 'Rekisteröinti epäonnistui!',
                    message: '',
                    success: false
                });
            }
        }
    }, []);

    if (settings?.registrationEnabled !== true) {
        return settingsLoading ? (
            <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                (odota hetki ladataan tietoja...)
            </Alert>
        ) : (
            <Container size="md">
                <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                    <Title order={1} style={{ paddingTop: 3, textAlign: 'center' }}>
                        Tammilaneille ilmoittautuminen on suljettu
                    </Title>
                </Paper>
                <Paper shadow="md" p="md" radius="md" withBorder>
                    <Title order={4} style={{ textAlign: 'center' }}>
                        Seuraavien lanien päivämäärää ei ole päätetty eikä ilmoittautuminen ole auki.
                        <br />
                        Seuraa Tammilanien viestikanavia...
                    </Title>
                </Paper>
            </Container>
        );
    }

    return settingsLoading ? (
        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
            (odota hetki ladataan tietoja...)
        </Alert>
    ) : (
        <Formik initialValues={initialValues} validationSchema={RegistrationSchema} onSubmit={storeRegistration}>
            {({ isSubmitting, errors }) => (
                <Form>
                    <Container size="md">
                        <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                            <Title order={1} style={{ paddingTop: 3, textAlign: 'center' }}>
                                Tammilaneille {eventTime} ilmoittautuminen
                            </Title>
                        </Paper>
                        <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                            <BasicInfo />
                        </Paper>
                        <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                            <SleepingInfo />
                        </Paper>
                        {settings?.attendancePerDayEnabled && (
                            <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                                <AttendanceDays />
                            </Paper>
                        )}
                        <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                            <ContactInfo />
                        </Paper>
                        <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                            <GuardianInfo />
                        </Paper>
                        <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                            <TableReservation />
                        </Paper>
                        {settings?.foodEnabled !== false && (
                            <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                                <Food />
                            </Paper>
                        )}
                        <Paper shadow="md" p="md" radius="md" withBorder mb="md">
                            <Feedback />
                        </Paper>
                        {Object.keys(errors).length > 0 && (
                            <div className="required errorMessage">
                                Lomakkeella on virheitä korjaa ne ennen lähetystä!
                            </div>
                        )}
                        <Button
                            color="violet.4"
                            size="lg"
                            type="submit"
                            disabled={isSubmitting || Object.keys(errors).length > 0}
                        >
                            Lähetä
                        </Button>
                        <Text mt="md">
                            <span style={{ color: 'grey' }}>* pakollinen tieto</span>
                        </Text>
                        {showMessage.message.length > 0 && (
                            <Alert
                                style={{ zIndex: 1000 }}
                                color={showMessage.success ? 'green' : 'red'}
                                icon={showMessage.success ? <IconCheck size={16} /> : <IconX size={16} />}
                                title={showMessage.title}
                                mt="md"
                            >
                                {showMessage.message}
                            </Alert>
                        )}
                    </Container>
                </Form>
            )}
        </Formik>
    );
};

export default RegistrationView;
